import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export type RecruiterJobInteraction = 'view' | 'click';

/** Jobs mirrored from recruiter_job_postings share the posting's id and carry
 *  board_source = 'recruiter'. Only those have a posting row behind the
 *  interactions foreign key, so only those are tracked. */
export function isRecruiterPosting(
  job: { board_source?: string | null } | null | undefined,
): boolean {
  return job?.board_source === 'recruiter';
}

/** Fire-and-forget. A trigger on recruiter_job_interactions bumps views_count
 *  and applications_count on the posting, which is what the recruiter
 *  dashboard shows. Works signed out: the insert policy is public, and the
 *  user id is attached when there is a session. */
export async function trackRecruiterJobInteraction(
  jobPostingId: string,
  interactionType: RecruiterJobInteraction,
): Promise<void> {
  try {
    const { data } = await supabase.auth.getSession();
    await supabase.from('recruiter_job_interactions').insert({
      job_posting_id: jobPostingId,
      user_id: data.session?.user?.id ?? null,
      interaction_type: interactionType,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      referrer: typeof document !== 'undefined' && document.referrer ? document.referrer : null,
    });
  } catch (error) {
    // Never let analytics disrupt the job page.
    console.warn('Failed to track job interaction:', error);
  }
}

export const useJobInteractionTracking = () => {
  const trackJobView = useCallback((jobPostingId: string) => {
    void trackRecruiterJobInteraction(jobPostingId, 'view');
  }, []);

  const trackJobClick = useCallback((jobPostingId: string) => {
    void trackRecruiterJobInteraction(jobPostingId, 'click');
  }, []);

  return {
    trackJobView,
    trackJobClick,
    trackInteraction: trackRecruiterJobInteraction,
  };
};
